import { ChangeDetectionStrategy, Component, Injector, OnInit } from "@angular/core";
import { CareerValuation } from "@common/ADAPT.Common.Model/organisation/career-valuation";
import { CulturalActivity } from "@common/ADAPT.Common.Model/organisation/cultural-activity";
import { CulturalActivityType } from "@common/ADAPT.Common.Model/organisation/cultural-activity-type";
import { CulturalIndex } from "@common/ADAPT.Common.Model/organisation/cultural-index";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { IGroupedData } from "@common/lib/utilities/grouped-data.interface";
import { emptyIfUndefinedOrNull } from "@common/lib/utilities/rxjs-utilities";
import { UserService } from "@common/user/user.service";
import { BaseRoutedComponent } from "@common/ux/base-routed.component";
import { IDxListSelectionChangedEvent } from "@common/ux/dx.types";
import { AuthorisationNotificationService } from "@org-common/lib/authorisation/authorisation-notification.service";
import { OrgCommonCareerValuationService } from "@org-common/lib/career-valuation/org-common-career-valuation.service";
import { CulturalActivityService } from "@org-common/lib/cultural-activity/cultural-activity.service";
import { CulturalActivityMetadataByType } from "@org-common/lib/cultural-activity/cultural-activity-type-metadata";
import { CulturalLeadershipFrameworkAuthService } from "@org-common/lib/cultural-leadership/cultural-leadership-framework-auth.service";
import { CulturalCohortTooltip, SupportingMemberTooltip } from "@org-common/lib/cultural-leadership/org-common-cultural-leadership.service";
import { OrganisationPageRouteBuilder } from "@org-common/lib/route/organisation-page-route-builder";
import { CareerValuationAuthService } from "app/features/people/career-valuation/career-valuation-auth.service";
import { CareerValuationUiService } from "app/features/people/career-valuation/career-valuation-ui.service";
import { CulturalIndexAuthService } from "app/features/people/cultural-index/cultural-index-auth.service";
import { CulturalIndexUiService } from "app/features/people/cultural-index/cultural-index-ui.service";
import DataSource from "devextreme/data/data_source";
import dxList from "devextreme/ui/list";
import { BehaviorSubject, lastValueFrom, merge, Observable } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { ICulturalCohortMemberData } from "../cultural-cohort-member-data.interface";
import { CulturalLeadershipFrameworkService } from "../cultural-leadership-framework.service";

export interface ISupportingMemberGroupedItem {
    key: number // culturalLeader.personId
    member: ICulturalCohortMemberData,
}

@Component({
    selector: "adapt-cultural-cohort-personal-page",
    templateUrl: "./cultural-cohort-personal-page.component.html",
    styleUrls: ["./cultural-cohort-personal-page.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class CulturalCohortPersonalPageComponent extends BaseRoutedComponent implements OnInit {
    public readonly CulturalCohortTooltip = CulturalCohortTooltip;
    public readonly SupportingMemberTooltip = SupportingMemberTooltip;

    public readonly EditCareerValuationForPerson = OrgCommonCareerValuationService.EditCareerValuationForPerson;
    public readonly EditCulturalIndexForPerson = CulturalIndexAuthService.EditCulturalIndexForPerson;

    public readonly CulturalActivityType = CulturalActivityType;

    public cohortName?: string;
    public cohort$?: Observable<ICulturalCohortMemberData[]>;
    public supportingMembers?: DataSource<ISupportingMemberGroupedItem>;
    public refreshRequired$ = new BehaviorSubject<undefined>(undefined);
    public selectedCohortMember: ICulturalCohortMemberData | undefined;
    public hasCohortMembers = true;
    public supportingMembersList?: dxList;

    public canReadCVDataForSelectedCohortMember = false;
    public canReadCIDataForSelectedCohortMember = false;

    constructor(
        injector: Injector,
        private culturalLeadershipFrameworkService: CulturalLeadershipFrameworkService,
        private careerValuationUiService: CareerValuationUiService,
        private culturalIndexUiService: CulturalIndexUiService,
        private userService: UserService,
        private rxjsBreezeService: RxjsBreezeService,
        private culturalActivityService: CulturalActivityService,
        private careerValuationAuthService: CareerValuationAuthService,
        private culturalIndexAuthService: CulturalIndexAuthService,
        authNotificationService: AuthorisationNotificationService,
    ) {
        super(injector);

        // if someone saves a career valuation, then refresh the cohort
        merge(
            this.rxjsBreezeService.entityTypeChanged(CareerValuation),
            this.rxjsBreezeService.entityTypeChanged(CulturalIndex),
            this.rxjsBreezeService.entityTypeChanged(CulturalActivity),
        ).pipe(
            tap(() => this.refreshRequired$.next(undefined)),
            this.takeUntilDestroyed(),
        ).subscribe();

        authNotificationService.authorisationChanged$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => this.refreshRequired$.next(undefined));
    }

    public async ngOnInit() {
        // build the cohort, if previous selection was active attempt to restore it
        this.cohort$ = this.refreshRequired$.pipe(
            map(this.userService.getCurrentPersonId),
            emptyIfUndefinedOrNull(),
            switchMap((personId) => this.culturalLeadershipFrameworkService.getCulturalCohortMemberData(personId)),
            tap((data) => {
                const groupItems: ISupportingMemberGroupedItem[] = [];
                const members = data.filter((d) => d.relationship.isSupporting);
                for (const m of members) {
                    groupItems.push({
                        key: m.primaryCulturalLeaderId ?? 0,
                        member: m,
                    });
                }
                this.supportingMembers = new DataSource<ISupportingMemberGroupedItem>({
                    store: groupItems,
                    group: (item: ISupportingMemberGroupedItem) => item.key,
                    sort: ["member.relationship.culturalLeader.firstName"],
                });
            }),
            map((data) => data.filter((p) => !p.relationship.isSupporting)),
            tap((data) => this.hasCohortMembers = data.length > 0),
            tap((data) => {
                if (this.selectedCohortMember?.relationship) {
                    this.updateSelectedCohortMember(data.find((element) => element.relationship.personId === this.selectedCohortMember?.relationship.personId));
                } else if (data.length) {
                    this.updateSelectedCohortMember(data[0]);
                } else {
                    setTimeout(() => {
                        const listItems = this.supportingMembersList?.getDataSource().items() as IGroupedData<number, ISupportingMemberGroupedItem>[];
                        this.updateSelectedCohortMember(listItems[0]?.items[0]?.member);
                    });
                }
            }),
        );

        const clConfig = await this.culturalLeadershipFrameworkService.promiseToGetCulturalLeadershipConfiguration();
        this.cohortName = clConfig.secondaryCollectionName;

        this.notifyActivated();
    }

    public async onPrimaryListSelectionChanged(e: IDxListSelectionChangedEvent<ICulturalCohortMemberData>) {
        if (e.addedItems.length) {
            await this.updateSelectedCohortMember(ArrayUtilities.getSingleFromArray(e.addedItems));
        }
    }

    public async onSelectionChanged(e: ICulturalCohortMemberData) {
        if (e) {
            await this.updateSelectedCohortMember(e);
        }
    }

    public async recordCVT() {
        const person = this.selectedCohortMember!.relationship.person;
        await this.careerValuationUiService.promiseToRecordCareerValuation(person);
    }

    public async recordCI() {
        const person = this.selectedCohortMember!.relationship.person;
        await this.culturalIndexUiService.promiseToRecordCulturalIndex(person);
    }

    public async rescheduleEvent(event: CulturalActivity) {
        await lastValueFrom(this.culturalActivityService.rescheduleActivity(event, true), { defaultValue: undefined });
    }

    public async scheduleEvent(eventType: CulturalActivityType) {
        if (this.selectedCohortMember) {
            const date = eventType === CulturalActivityType.CareerValuation
                ? this.selectedCohortMember.nextCVT
                : this.selectedCohortMember.nextCI;
            await lastValueFrom(this.culturalActivityService.scheduleActivity(eventType, this.selectedCohortMember.relationship.person, this.userService.currentPerson!, date), { defaultValue: undefined });
        }
    }

    public getFullTypeName(type: CulturalActivityType) {
        // show as Title Case
        return CulturalActivityMetadataByType[type].shortTitle.replace(/\b[a-z]/g, (x) => x.toUpperCase());
    }

    private async updateSelectedCohortMember(member?: ICulturalCohortMemberData) {
        this.selectedCohortMember = member;
        if (this.userService.currentPerson && this.selectedCohortMember?.relationship?.person) {
            this.canReadCVDataForSelectedCohortMember = await this.careerValuationAuthService.personCanReadForPerson(
                this.userService.currentPerson, this.selectedCohortMember.relationship.person);
            this.canReadCIDataForSelectedCohortMember = await this.culturalIndexAuthService.personCanReadForPerson(
                this.userService.currentPerson, this.selectedCohortMember.relationship.person);
        } else {
            this.canReadCVDataForSelectedCohortMember = false;
            this.canReadCIDataForSelectedCohortMember = false;
        }
    }
}


export const CulturalCohortPersonalPageRoute = new OrganisationPageRouteBuilder()
    .usingNgComponent("adapt-cultural-cohort-personal-page", CulturalCohortPersonalPageComponent)
    .atOrganisationUrl("/people/cultural-leadership")
    .withTitle("Cultural Leadership")
    .verifyingAccess(CulturalLeadershipFrameworkAuthService.ReadCohorts)
    .reloadOnSearch(false)
    .build();
